'use strict';
//***********************************************************************************
//***********************************************************************************
//**** cn_svg_tool_zone  : Manipulation of zone
//***********************************************************************************
//***********************************************************************************

import { cn_wall } from '../model/cn_wall';
import { cn_svg_tool_creation } from './cn_svg_tool_creation';
import { cn_svg_map } from './cn_svg_map';
import { cn_camera } from './cn_camera';
import { cn_box, cn_space, cn_storey, cn_zone, extension_instance, logger } from '..';
import { zone_colors } from '../utils/cn_zone_color';
import { HELPER } from '../utils/cn_wordings';

/**
 * Events emitted by the tool:
 * - 'association' with parameter cn_space, when a space is associated to a zone.
 * - 'creation' with paramater cn_zone, when a new zone is created.
 */
export class cn_svg_tool_zone extends cn_svg_tool_creation {
    //***********************************************************************************
    /**
     * Constructor
     * @param {cn_svg_map} svg_map
     */
    constructor(svg_map, zoning_type) {
        super(svg_map);
        this.svg_map = svg_map;
        this.current_zone = null;
        this.replicate_on_clone = false;
        this.set_zoning_type(zoning_type);
        this._mouseover_space = null;
        this.element_filter = function (element) {
            return element.constructor == cn_wall
        };
    }

    //***********************************************************************************
    /**
     * Open tool
     */
    open_tool() {
        super.open_tool();
        this.push_instruction();
    }

    set_zoning_type(zoning_type) {
        this.zoning_type = zoning_type;
        if (!this._building.zones[this.zoning_type]) {
            this._building.zones[this.zoning_type] = [];
        }
        this.push_instruction();
    }

    push_instruction() {
        if (this._building.zones[this.zoning_type] && this._building.zones[this.zoning_type].length) {
            if (this.current_zone) {
                this.push_instruction_input(HELPER.zone.creation);
            } else {
                this.push_instruction_input(HELPER.zone.select);
            }
        } else {
            this.push_instruction_input(HELPER.zone.empty_state);
        }
    }

    //***********************************************************************************
    /**
     * SVG rendering
     * @param {cn_camera} camera
     * @returns {string} svg rendered
     */
    draw(camera) {
        var html = '';
        if (this._mouseover_space) {
            html += this._mouseover_space.draw(camera, ['mouseover']);
        }
        return html;
    }

    clear_move() {
        super.clear_move();
        this._mouseover_space = null;
    }

    //***********************************************************************************
    /**
     * click management
     * @param {object} ev
     * @returns {boolean} true if click was used
     */
    click(ev) {
        if (this.current_zone) {
            const selected_space = this._find_mouseover_space(ev);
            if (selected_space) {
                this.associate_spaces_to_zone([selected_space]);
                this.call("association",selected_space);
            }
        }
        return true;
    }

    /**
     * Associate spaces into current zone (or other if specified).
     * If no spaces are specified, all current storey spaces are associated.
     *
     * @param {cn_space[]} selected_spaces
     * @param {string} zone_id
     * @param {cn_storey} storey
     * @param {boolean} consider_clones
     * @param {boolean} push_transaction
     */
    associate_spaces_to_zone(selected_spaces = null, zone_id = this.current_zone, storey = this._storey, consider_clones = true, push_transaction = true) {
        const current_cn_zone = this._building.zones[this.zoning_type].find(zone => zone.ID === zone_id);

        if (!current_cn_zone) return;

        if (push_transaction) {
            this.push_transaction(`Ajout de pièces dans une zone`);
        }

        const clone_storeys = (this.replicate_on_clone && current_cn_zone.main_storey == storey.ID) ? storey.building.storeys.filter(s => s != storey && s.scene == storey.scene) : [];

        const spaces_to_associate = selected_spaces || storey.scene.spaces.filter(space => !space.outside);
        const toggle_mode = selected_spaces != null && spaces_to_associate.length == 1; // if manual single space association, toggle mode, else only associate (no disassociation)

        const transaction_manager = storey.building.transaction_manager;
        spaces_to_associate.forEach(space_to_associate => {

            if (toggle_mode && space_to_associate.get_zone(this.zoning_type, storey) == current_cn_zone) {
                space_to_associate.set_zone(this.zoning_type, null, storey, transaction_manager);
                if (consider_clones) {
                    clone_storeys.forEach(other_storey => space_to_associate.set_zone(this.zoning_type, null, other_storey, transaction_manager));
                }
            } else {
                space_to_associate.set_zone(this.zoning_type, current_cn_zone, storey, transaction_manager);
                if (consider_clones) {
                    clone_storeys.forEach(other_storey => {
                        const matching_cn_zone = this._search_corresponding_zone_on_storey(current_cn_zone, other_storey);
                        if (matching_cn_zone)
                            space_to_associate.set_zone(this.zoning_type, matching_cn_zone, other_storey, transaction_manager);
                    });
                }
            }

        });
    }

    /**
     * Push rooms in transaction item
     *
     * @param {cn_zone} current_zone
     */
    _push_item_rooms_of_zone(current_zone) {
        const zone_index = this._building.zones[this.zoning_type].findIndex(zone => zone.ID === current_zone.ID);
        this.push_item_set(this._building.zones[this.zoning_type][zone_index], 'rooms');
    }

    /**
     * Remove equivalent rooms to cloned storeys' equivalent zones
     *
     * @param {cn_storey} current_storey
     * @param {cn_zone} existing_zone_for_space
     * @param {cn_space} selected_space
     */
    _handle_remove_room_on_clone(current_storey, existing_zone_for_space, selected_space) {
        if (this.replicate_on_clone && current_storey.ID === existing_zone_for_space.main_storey && current_storey.clone_of !== null) {
            this._get_cloned_storeys(current_storey).forEach(cloned_storey => {
                const corresponding_zone = this._search_corresponding_zone_on_storey(existing_zone_for_space, cloned_storey);
                if (corresponding_zone) {
                    this._push_item_rooms_of_zone(corresponding_zone);
                    logger.log('disassociate zone ' + corresponding_zone.name + ' from space ' + selected_space.get_name(current_storey) + ' on cloned storey ' + cloned_storey.get_storey_name());
                    corresponding_zone.remove_room(selected_space.ID, cloned_storey.ID);
                }
            });
        }
    }

    /**
     * Add equivalent rooms to cloned storeys
     *
     * @param {cn_storey} current_storey
     * @param {cn_zone} current_zone_building
     * @param {cn_space} selected_space
     */
    _handle_add_room_on_clone(current_storey, current_zone_building, selected_space) {
        if (this.replicate_on_clone && current_storey.ID === current_zone_building.main_storey && current_storey.clone_of !== null) {
            this._get_cloned_storeys(current_storey).forEach(cloned_storey => {
                const existing_zone_for_space_in_clone_storey = this._search_zone_with_space_in_storey(selected_space, cloned_storey);
                if (existing_zone_for_space_in_clone_storey && existing_zone_for_space_in_clone_storey.main_storey === cloned_storey.ID) {
                    this._push_item_rooms_of_zone(existing_zone_for_space_in_clone_storey);
                    logger.log('disassociate zone ' + existing_zone_for_space_in_clone_storey.name + ' from space ' + selected_space.get_name(current_storey) + ' on cloned storey ' + cloned_storey.get_storey_name());
                    existing_zone_for_space_in_clone_storey.remove_room(selected_space.ID, cloned_storey.ID);
                }
                if (!existing_zone_for_space_in_clone_storey || existing_zone_for_space_in_clone_storey.main_storey === cloned_storey.ID) {
                    const corresponding_zone = this._search_corresponding_zone_on_storey(current_zone_building, cloned_storey);
                    if (corresponding_zone) {
                        this._push_item_rooms_of_zone(corresponding_zone);
                        logger.log('associate zone ' + corresponding_zone.name + ' to space ' + selected_space.get_name(current_storey) + ' on cloned storey ' + cloned_storey.get_storey_name());
                        corresponding_zone.add_room(selected_space.ID, cloned_storey.ID);
                    }
                }
            });
        }
    }

    /**
     * Search zone which may refer that room (space and storey)
     *
     * @param {cn_space} selected_space
     * @param {cn_storey} storey
     * @returns
     */
    _search_zone_with_space_in_storey(selected_space, storey) {
        return this._building.zones[this.zoning_type].find(zone => zone.rooms.find(room => room.space === selected_space.ID && room.storey === storey.ID));
    }

    /**
     * Get list of cloned storeys of current storey
     *
     * @param {cn_storey} current_storey
     * @returns
     */
    _get_cloned_storeys(current_storey) {
        return this._building.storeys.filter(storey => storey.storey_index !== current_storey.storey_index
            && storey.clone_of !== null && storey.clone_of.ID === current_storey.clone_of.ID);
    }

    /**
     * Search equivalent zone of defined zone in targetted storey
     * (clone algorithm)
     *
     * @param {cn_zone} zone
     * @param {cn_storey} storey
     * @returns
     */
    _search_corresponding_zone_on_storey(zone, storey) {
        let result = null;
        const eligibles_zones = this._building.zones[this.zoning_type].filter(z => z.main_storey === storey.ID);
        if (zone.rooms.length === 0) {
            const empty_zones = eligibles_zones.filter(z => z.rooms.length === 0);
            if (empty_zones.length === 1) {
                result = empty_zones[0]
            } else {
                result = empty_zones.find(z => z.name === zone.name);
            }
        } else {
            const current_storey_rooms = zone.rooms.filter(room => room.storey === this._storey.ID).map(room => room.space);
            result = eligibles_zones.find(z => z.rooms.filter(room => room.storey === z.main_storey).every(room => current_storey_rooms.includes(room.space)));
        }
        return result;
    }

    //***********************************************************************************
    /**
     * passive move management
     * @param {object} ev
     * @returns {boolean} true if passive move was used
     */
    move(ev) {
        if (this.current_zone) {
            this._mouseover_space = this._find_mouseover_space(ev);
        }
        return true;
    }

    _find_mouseover_space(ev) {
        var space = this._scene.find_space(ev.mouse_world);
        if (!space || space.outside) return null;
        while (space && space.parent_space)
            space = space.parent_space;
        return space;
    }

    /**
     *
     * @param {string} zoneLabel
     * @param {cn_storey} storey
     * @param {boolean} consider_clones
     * @returns {cn_zone}
     * @private
     */
    _get_or_create_zone(zoneLabel, storey, consider_clones) {
        return this._building.get_zones(this.zoning_type).find(it => it.main_storey == storey.ID && it.name == zoneLabel)
            ?? this.add_zone_on_other_storey(zoneLabel, '', storey, consider_clones, false);
    }

    /**
     * @param {boolean} currentStoreyOnly
     * @param {string} labelHeated
     * @param {string} labelUnheated
     */
    set_from_heated_spaces(currentStoreyOnly, labelHeated, labelUnheated) {

        const set_for_storey = (/** @type {cn_storey} */ storey, /** @type {boolean} */ consider_clones) => {
            const zoneHeated = this._get_or_create_zone(labelHeated, storey, consider_clones);
            const zoneUnheated = this._get_or_create_zone(labelUnheated, storey, consider_clones);
            this.associate_spaces_to_zone(storey.scene.spaces.filter(it => it.is_heated()), zoneHeated.ID, storey, consider_clones, false);
            this.associate_spaces_to_zone(storey.scene.spaces.filter(it => !it.is_heated()), zoneUnheated.ID, storey, consider_clones, false);
        }

        if (currentStoreyOnly) {
            this.push_transaction(`Copie des zones thermiques pour le niveau ${this._storey.get_storey_name()}`);
            set_for_storey(this._storey, true);
        } else {
            this.push_transaction(`Copie des zones thermiques pour le bâtiment`);
            this._building.storeys.forEach(storey => set_for_storey(storey, false));
        }
    }

    /**
     * Copy zones from another zoning type
     * @param {boolean} currentStoreyOnly
     * @param {string} otherZoningType
     */
    set_from_other_zoning_type(currentStoreyOnly, otherZoningType) {

        const set_for_storey = (/** @type {cn_storey} */ storey, /** @type {boolean} */ consider_clones) => {
            const zonesForMainToolAndCurrentStorey = this._building.get_zones(otherZoningType).filter(it => it.main_storey == storey.ID);
            zonesForMainToolAndCurrentStorey.forEach(zone => {
                const zoneCopy = this._get_or_create_zone(zone.name, storey, consider_clones);
                this.associate_spaces_to_zone(storey.scene.spaces.filter(it => zone.rooms.map(r => r.space).includes(it.ID)), zoneCopy.ID, storey, consider_clones, false);
            });
        }

        if (currentStoreyOnly) {
            this.push_transaction(`Copie de zones pour le niveau ${this._storey.get_storey_name()}`);
            set_for_storey(this._storey, true);
        } else {
            this.push_transaction(`Copie  de zones pour le bâtiment`);
            this._building.storeys.forEach(storey => set_for_storey(storey, false));
        }
    }

    /**
     * Create a new zone on another storey
     *
     * @param {string} zone_name
     * @param {string} zone_type
     * @param {cn_storey} storey
     * @param {boolean} consider_clones
     * @param {boolean} push_transaction
     * @return {cn_zone}
     */
    add_zone_on_other_storey(zone_name, zone_type = '', storey, consider_clones = true, push_transaction = true) {
        return this._add_zone(zone_name, zone_type, storey, consider_clones, push_transaction);
    }

    /**
     * Create a new zone
     *
     * @param {string} zone_name
     * @param {string} zone_type
     * @return {cn_zone}
     */
    add_zone(zone_name, zone_type = '') {
        return this._add_zone(zone_name, zone_type, this._storey, true, true);
    }

    /**
     * Create a new zone
     *
     * @param {string} zone_name
     * @param {string} zone_type
     * @param {cn_storey} storey
     * @param {boolean} consider_clones
     * @param {boolean} push_transaction
     * @return {cn_zone}
     */
    _add_zone(zone_name, zone_type = '', storey, consider_clones = true, push_transaction = true) {
        if (push_transaction) {
            this.push_transaction(`Création d'une nouvelle zone`);
        }
        this.push_item_set(this._building.zones, this.zoning_type);
        const new_zone = new cn_zone(zone_name, storey.ID, zone_type);
        new_zone.color = zone_colors[this._building.zones[this.zoning_type].length % 10];
        this._building.zones[this.zoning_type].push(new_zone);
        if (storey.clone_of !== null && consider_clones === true) {
            this._building.storeys
                .filter(it => it.storey_index !== storey.storey_index && it.clone_of !== null && it.clone_of.ID === storey.clone_of.ID)
                .forEach(it => {
                    const new_zone_for_clone = new cn_zone(zone_name, it.ID);
                    new_zone_for_clone.color = zone_colors[this._building.zones[this.zoning_type].length % 10];
                    this._building.zones[this.zoning_type].push(new_zone_for_clone);
                });
        }
        this.call("creation",new_zone);
        return new_zone;
    }

    /**
     *
     * Modify a zone
     *
     * @param {cn_zone} zone
     * @param {string} new_name
     * @param {string} new_color
     * @param {string} zone_type
     */
    modify_zone(zone, new_name, new_color, zone_type = '', space_auto_numerotation_idx = 0) {
        const old_zone_index = this._building.zones[this.zoning_type].findIndex(z => zone.ID === z.ID);
        this.push_transaction(`Modification d'une zone`);
        this.push_item_set(this._building.zones[this.zoning_type][old_zone_index], 'name');
        this.push_item_set(this._building.zones[this.zoning_type][old_zone_index], 'color');
        this.push_item_set(this._building.zones[this.zoning_type][old_zone_index], 'zone_type');
        const old_zone = this._building.zones[this.zoning_type][old_zone_index];
        old_zone.name = new_name;
        old_zone.zone_type = zone_type;
        if (new_color) {
            old_zone.color = new_color;
        }
    }

    /**
     * Delete zone by id
     *
     * @param {string} zone_id
     */
    delete_zone(zone_id) {
        this.push_transaction(`Suppression d'une zone`);
        this.push_item_set(this._building.zones, this.zoning_type);
        if (this._storey.clone_of !== null) {
            const current_zone = this._building.zones[this.zoning_type].find(zone => zone.ID === zone_id);
            this._building.storeys.filter(storey => storey.storey_index !== this._storey.storey_index
                && storey.clone_of !== null && storey.clone_of.ID === this._storey.clone_of.ID).forEach(storey => {
                const corresponding_zone = this._search_corresponding_zone_on_storey(current_zone, storey);
                if (corresponding_zone) {
                    this._building.zones[this.zoning_type].splice(
                        this._building.zones[this.zoning_type].findIndex(zone => zone.ID === corresponding_zone.ID), 1);
                }
            });
        }

        const zone_index = this._building.zones[this.zoning_type].findIndex(zone => zone.ID === zone_id);
        if (zone_index >= 0) {
            extension_instance.space.on_delete_zone(this._building.zones[this.zoning_type][zone_index], this._building.transaction_manager);
            this._building.zones[this.zoning_type].splice(zone_index, 1);
            this.current_zone = null;
        }
    }

    /**
     * Set current zone id
     *
     * @param {string} current_zone
     */
    set_current_zone(current_zone) {
        this.current_zone = current_zone;
        this.push_instruction();
    }

    /**
     * Set replicate clone mode
     *
     * @param {boolean} is_active
     */
    set_replicate_clone_mode(is_active) {
        this.replicate_on_clone = is_active;
    }

    /**
     * Center camera on selected zone
     *
     * @param zone_id
     * @param storey
     */
    center_camera_on_selected_zone(zone_id, storey) {
        const current_zone = this._building.zones[this.zoning_type].find(zone => zone.ID === zone_id);
        if (current_zone && current_zone.rooms && current_zone.rooms.length) {
            const rooms = current_zone.rooms.map(r => storey.scene.spaces.find(s => s.ID === r.space));
            if (rooms.length) {
                const bb = this.get_box(rooms);
                this.svg_map.center_camera_on_custom_bounding_box(bb);
            }
        }
    }

    /**
     * get bounding box of an array of spaces
     * @param spaces
     * @returns {cn_box}
     */
    get_box(spaces) {
        let box = new cn_box();
        for (const s in spaces) {
            if (spaces[s]) {
                for (var i in spaces[s].contours) {
                    box.enlarge_box(spaces[s].contours[i].get_bounding_box());
                }
            }
        }
        return box;
    }
}

